import { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";

import { colors } from "../../consts";
import { useOnClickOutside } from "../../hooks";
import { dropdownUtil } from "../../utils";

import {
  DropdownOptions,
  FlexContainers,
  IconButton,
  SearchInput,
  SVG,
  WrapperContainer
} from "../index";
import SelectedItemsList from "./SelectedItemsList";
import { DropdownMultiselectWrapper } from "./styled";

const DropdownMultiselectInput = ({
  id,
  title,
  warningMsg,
  placeholder,
  options,
  values = [],
  setValues,
  valueLabel,
  disabled,
  checkIsOptionDisabled
}) => {
  const [searchText, setSearchText] = useState("");
  const [showSearchField, setShowSearchField] = useState(true);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [formattedOptions, setFormattedOptions] = useState([]);
  const [isOptionsVisible, setIsOptionsVisible] = useState(false);

  const containerRef = useRef();
  useOnClickOutside(containerRef, () => {
    setSearchText("");
    setShowSearchField(false);
    setIsOptionsVisible(false);
  });

  const availableOptionIds = useMemo(() => {
    return formattedOptions.filter((option) => option.available).map((option) => option.id);
  }, [formattedOptions]);

  const availableOptionsToDeleteIds = useMemo(() => {
    return values
      .filter((value) => !formattedOptions.some((option) => option.id === value.id))
      .map((value) => value.id);
  }, [formattedOptions, values]);

  useEffect(() => {
    const filteredOptions = formattedOptions.filter(
      (option) => option.available && option.value.toLowerCase().includes(searchText.toLowerCase())
    );

    setFilteredOptions(filteredOptions);
  }, [searchText, formattedOptions]);

  useEffect(() => {
    setFormattedOptions(
      options.map((item) => {
        return {
          ...item,
          value: `${item.value}${valueLabel ? ` - ${valueLabel(item.value)}` : ""}`
        };
      })
    );
  }, [options]);

  const handleSearchIconClick = () => {
    setSearchText("");
    setShowSearchField((prev) => !prev);
  };

  const handleSelectOption = (id) => {
    setValues(dropdownUtil.updateSelectedOptions(id, values, formattedOptions, true));
    setSearchText("");
  };

  const setValuesAvailablility = (values) => {
    return values.map((value) => {
      return {
        ...value,
        available:
          availableOptionIds.includes(value.id) || availableOptionsToDeleteIds.includes(value.id)
      };
    });
  };

  return (
    <DropdownMultiselectWrapper
      innerRef={containerRef}
      Container={FlexContainers.FlexColumnStart}
      width="100%"
      height="100%"
      position="relative"
      onClick={() => !disabled && setIsOptionsVisible(true)}
      active={isOptionsVisible}>
      <SearchInput
        id={id}
        title={title}
        warningMsg={warningMsg}
        showSearchIcon={false}
        value={searchText}
        setValue={setSearchText}
        placeholder={placeholder}
        showSearch={showSearchField}
        onSearchIconClick={handleSearchIconClick}
        disabled={disabled}
        icon={
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setIsOptionsVisible((prev) => !prev);
            }}>
            <SVG.DownArrowIcon color={colors.RAISIN_50} flipVert={isOptionsVisible} />
          </IconButton>
        }
        component={
          values?.length > 0 && (
            <SelectedItemsList
              items={setValuesAvailablility(values)}
              onDelete={handleSelectOption}
              id={"markets-selector"}
              value={searchText}
              setValue={setSearchText}
              checkIsOptionDisabled={checkIsOptionDisabled}
            />
          )
        }
      />

      <WrapperContainer width="100%" position="relative">
        <DropdownOptions
          visible={isOptionsVisible}
          options={filteredOptions}
          selectedOptions={values}
          onSelectOption={handleSelectOption}
          searchText={searchText}
          showCheckboxes
        />
      </WrapperContainer>
    </DropdownMultiselectWrapper>
  );
};

DropdownMultiselectInput.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  warningMsg: PropTypes.string,
  placeholder: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.string.isRequired,
      available: PropTypes.bool.isRequired
    })
  ).isRequired,
  values: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.string.isRequired
    })
  ).isRequired,
  setValues: PropTypes.func.isRequired,
  valueLabel: PropTypes.func,
  disabled: PropTypes.bool,
  checkIsOptionDisabled: PropTypes.func
};

export default DropdownMultiselectInput;
